/**
 * Hono module route setup.
 *
 * Mirrors `src/core/module-routes.ts` (Express) with these responsibilities:
 *  0. Pre-register entity services so module.init() can read them.
 *  1. Initialize modules (register middleware, services).
 *  1a. Validate and inject workflow fields.
 *  1b/1c. Build CASL registry and field-deny registry. Register injected actions.
 *  1d. Auto-register entity access checks.
 *  2. Mount custom routes, standalone actions, and auto-generated entity
 *     routers on the Hono app.
 *
 * Rendered Express-agnostic by using Hono routers (`new Hono<NexusEnv>()`)
 * throughout. Modules that expose `module.routes(ctx)` returning a Hono
 * instance will mount directly; modules that still return Express routers
 * remain broken until F3 (documented).
 */

import type { EntityDefinition, ModuleContext, EntityCaslConfig, ActionDefinition, WorkflowConfig } from '@gzl10/nexus-sdk'
import { resolveLocalized } from '@gzl10/nexus-sdk'
import type { RegisteredModule } from '@kernel/engine/module-store.js'
import { getOrderedModulesInternal } from '@kernel/engine/index.js'
import { createModuleContext } from '@kernel/engine/context.js'
import { setEntityDefinitions, setFieldDenyRules } from '@kernel/abilities/ability.factory.js'
import { buildFieldDenyRegistry } from '@kernel/abilities/field-access.js'
import { registerEntityAccessChecks } from '../../core/auto-entity-access.js'
import { injectWorkflowField, validateWorkflowConfig } from '@kernel/runtime/workflow/index.js'
import { createModuleServices, getServiceKey } from '../../runtime/entity-factory.js'

import type { HonoApp, NexusEnv } from '../types.js'
import { createHonoRouter } from '../types.js'
import { createEntityRuntime } from '../entity-factory.js'
import { createActionRouters } from './action.routes.js'
import { inferEntityRoutePath } from '../openapi/utils.js'

/** Entity types that should always be auto-mounted regardless of custom routes. */
const ALWAYS_MOUNT_TYPES = ['view', 'computed', 'virtual', 'external', 'reference', 'event', 'tree', 'dag', 'temp']

/** Plugin-aware `ctx.db.t` scoping — same logic as the Express version. */
function createScopedContext(ctx: ModuleContext, mod: RegisteredModule): ModuleContext {
  if (!mod._tablePrefix || !mod._pluginTables) return ctx
  const prefix = mod._tablePrefix
  const pluginTables = mod._pluginTables
  return {
    ...ctx,
    db: {
      ...ctx.db,
      t: (name: string) => (pluginTables.has(name) ? `${prefix}${name}` : name)
    }
  }
}

/**
 * Build a Hono sub-router with all exposed entities of a module mounted on
 * their inferred sub-paths. Mirrors `createModuleRouters` from runtime/.
 */
async function buildModuleEntityRouter(
  ctx: ModuleContext,
  definitions: EntityDefinition[],
  modulePrefix?: string
) {
  const router = createHonoRouter()
  const routeMap = new Map<string, string>()

  const entries = definitions.map(definition => ({
    definition,
    route: inferEntityRoutePath(definition)
  }))
  entries.sort((a, b) => {
    if (a.route === '/') return 1
    if (b.route === '/') return -1
    return b.route.length - a.route.length
  })

  for (const { definition, route } of entries) {
    const key = getServiceKey(definition)
    // Use the pre-registered service if available (custom init services take priority over generic factory).
    const preRegistered = ctx.services.has(key) ? ctx.services.get(key) as import('@gzl10/nexus-sdk').BaseEntityService<Record<string, unknown>> : undefined
    const runtime = await createEntityRuntime(ctx, definition, undefined, preRegistered)
    const entityLabel = resolveLocalized(definition.label, 'en')
    const isExposed = !('expose' in definition && definition.expose === false)

    // Register service if not already there.
    if (!preRegistered) {
      ctx.services.register(key, runtime.service as never)
    } else {
      ctx.core.logger.debug({ key, entity: definition.label }, 'Using pre-registered service for entity router')
    }

    // Skip HTTP routes for expose: false entities — no endpoints, no UI visibility.
    if (!isExposed) continue

    if (modulePrefix && route === modulePrefix) {
      ctx.core.logger.warn(
        `Entity "${entityLabel}" inferred route "${route}" duplicates module prefix — add routePrefix to the entity definition to fix`
      )
    }
    if (routeMap.has(route)) {
      ctx.core.logger.warn(
        `Entity "${entityLabel}" route "${route}" collides with "${routeMap.get(route)}" in the same module`
      )
    }
    routeMap.set(route, entityLabel)

    router.route(route, runtime.router)
  }

  return router
}

/**
 * Setup module routes on the Hono app.
 *
 * Runs all boot steps (CASL registry, workflow injection, service pre-reg,
 * init hooks) and mounts custom + standalone + entity routers.
 */
export async function setupModuleRoutes(app: HonoApp): Promise<ModuleContext> {
  const ctx = createModuleContext()
  const modules = getOrderedModulesInternal()

  // 0. Pre-register entity services (some module.init() reads them).
  for (const mod of modules) {
    if (!mod.definitions?.length) continue
    const scopedCtx = createScopedContext(ctx, mod)
    const services = await createModuleServices(scopedCtx, mod.definitions)
    for (const [key, service] of services) {
      if (!ctx.services.has(key)) {
        ctx.services.register(key, service)
      }
    }
  }

  // 1. module.init() — registers middleware, services, event listeners.
  for (const mod of modules) {
    if (mod.init) {
      await mod.init(createScopedContext(ctx, mod))
    }
  }

  // 1a. Validate and inject workflow fields.
  for (const mod of modules) {
    for (const def of mod.definitions ?? []) {
      const workflow = (def as { workflow?: WorkflowConfig }).workflow
      if (!workflow) continue
      const entityName = ('table' in def ? (def as { table: string }).table : undefined) ?? mod.name
      validateWorkflowConfig(workflow, entityName)
      const fields = (def as { fields: Record<string, unknown> }).fields
      injectWorkflowField(fields, workflow)
      const casl = (def as { casl?: unknown }).casl
      if (!casl) {
        ctx.core.logger.warn(`Entity '${entityName}' has workflow but no CASL config — transition permissions won't be enforced`)
      }
    }
  }

  // 1b. Build CASL registry from entity + action definitions.
  type CaslRegistryEntry = Parameters<typeof setEntityDefinitions>[0] extends Map<string, infer V> ? V : never
  const caslRegistry = new Map<string, CaslRegistryEntry>()

  function mergeActionPermissions(
    existing: CaslRegistryEntry['permissions'],
    incoming: Record<string, unknown>
  ): CaslRegistryEntry['permissions'] {
    const merged = { ...existing } as Record<string, unknown>
    for (const [role, perm] of Object.entries(incoming)) {
      if (!merged[role]) {
        merged[role] = perm
      } else {
        const existingArr = Array.isArray(merged[role]) ? merged[role] : [merged[role]]
        const incomingArr = Array.isArray(perm) ? perm : [perm]
        merged[role] = [...existingArr, ...incomingArr]
      }
    }
    return merged as CaslRegistryEntry['permissions']
  }

  function registerActionCasl(action: ActionDefinition): void {
    const casl = action.casl
    if (!casl || !('subject' in casl) || !casl.subject || !('permissions' in casl) || !casl.permissions) return
    const subject = casl.subject
    const existing = caslRegistry.get(subject)
    if (existing) {
      existing.permissions = mergeActionPermissions(existing.permissions, casl.permissions)
    } else {
      caslRegistry.set(subject, {
        subject,
        permissions: (casl.permissions ?? {}) as CaslRegistryEntry['permissions']
      })
    }
  }

  for (const mod of modules) {
    for (const def of mod.definitions ?? []) {
      const casl = (def as { casl?: EntityCaslConfig }).casl
      if (!casl?.subject) continue
      const key = ('table' in def && (def as { table?: string }).table)
        ? (def as { table: string }).table
        : casl.subject
      caslRegistry.set(key, {
        subject: casl.subject,
        permissions: (casl.permissions ?? {}) as CaslRegistryEntry['permissions']
      })
      for (const action of (def as { actions?: ActionDefinition[] }).actions ?? []) {
        registerActionCasl(action)
      }
    }
    for (const action of mod.actions ?? []) {
      registerActionCasl(action)
    }
  }

  // 1c. Cross-module action injections.
  const moduleIndex = new Map(modules.map(m => [m.name, m]))
  for (const mod of modules) {
    if (!mod.injectActions?.length) continue
    for (const injection of mod.injectActions) {
      const targetMod = moduleIndex.get(injection.target.module)
      if (!targetMod) {
        ctx.core.logger.warn(`injectActions: target module '${injection.target.module}' not found (source: ${mod.name})`)
        continue
      }
      const targetDef = targetMod.definitions?.find(
        d => 'table' in d && (d as { table: string }).table === injection.target.entity
      ) as { actions?: ActionDefinition[] } | undefined
      if (!targetDef) {
        ctx.core.logger.warn(`injectActions: entity '${injection.target.entity}' not found in module '${injection.target.module}' (source: ${mod.name})`)
        continue
      }
      if (!targetDef.actions) targetDef.actions = []
      if (!targetDef.actions.some(a => a.key === injection.action.key)) {
        targetDef.actions.push(injection.action)
        registerActionCasl(injection.action)
      }
    }
  }

  setEntityDefinitions(caslRegistry)

  // 1c-bis. Field-deny registry.
  setFieldDenyRules(buildFieldDenyRegistry(modules))

  // 1d. Auto entity access checks.
  registerEntityAccessChecks(ctx, modules)

  // 2. Mount routes.
  for (const mod of modules) {
    if (mod._disableEndpoints) continue

    const routePrefix = mod.routePrefix ?? `/${mod.name}`
    const scopedCtx = createScopedContext(ctx, mod)
    const mountPath = `/api/v1${routePrefix}`

    // 2a. Custom routes (Hono sub-apps).
    if (mod.routes) {
      // mod.routes(ctx) returns Hono<any> per SDK typing. Modules that need
      // OpenAPI features cast to OpenAPIHono themselves. Double cast via
      // unknown is still needed while legacy modules return Express Router
      // at runtime — Fase B migrates them to Hono.
      const customRouter = mod.routes(scopedCtx) as unknown as HonoApp
      app.route(mountPath, customRouter)
    }

    // 2b. Standalone actions.
    if (mod.actions && mod.actions.length > 0) {
      const actionRouter = createActionRouters(scopedCtx, mod.actions)
      app.route(mountPath, actionRouter as unknown as HonoApp)
    }

    // 2c. Auto-generated entity routers.
    if (mod.definitions && mod.definitions.length > 0) {
      const alwaysMountable = mod.definitions.filter(d => ALWAYS_MOUNT_TYPES.includes(d.type ?? ''))
      if (alwaysMountable.length > 0) {
        const always = await buildModuleEntityRouter(scopedCtx, alwaysMountable, routePrefix)
        app.route(mountPath, always)
      }
      const autoMountable = mod.definitions.filter(d => {
        const type = d.type ?? 'collection'
        return type === 'collection' || type === 'single' || type === 'config'
      })
      if (autoMountable.length > 0) {
        const auto = await buildModuleEntityRouter(scopedCtx, autoMountable, routePrefix)
        app.route(mountPath, auto)
      }
    }
  }

  return ctx
}

// Silence unused import when NexusEnv isn't consumed by TS in this file.
type _KeepNexusEnv = NexusEnv
